// pages/app/Notes.tsx — free-form notes via TipTap; LLM refines then extracts (spec §6.2).
import { useState, type FormEvent } from 'react';
import { EditorContent, useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { useServices } from '../../app/AppContext';
import { SourceList } from '../../components/SourceList';
import type { Source } from '../../lib/api';

export function Notes() {
  const { api } = useServices();
  const editor = useEditor({ extensions: [StarterKit], content: '' });
  const [title, setTitle] = useState('');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  function startEdit(s: Source) {
    const text = typeof s.meta.text === 'string' ? s.meta.text : '';
    setTitle(s.label ?? '');
    editor?.commands.setContent(text);
    setEditingId(s.id);
    setMessage(null);
    setError(null);
  }

  function cancelEdit() {
    setEditingId(null);
    setTitle('');
    editor?.commands.clearContent();
  }

  async function submit(e: FormEvent) {
    e.preventDefault();
    const text = (editor?.getText() ?? '').trim();
    if (!text) return;
    setBusy(true);
    setMessage(null);
    setError(null);
    try {
      if (editingId) {
        await api.updateNote(editingId, { text, title: title || undefined });
        setMessage('Note updated — re-extracting.');
      } else {
        await api.ingestNote({ text, title: title || undefined });
        setMessage('Note captured — refining and extracting.');
      }
      editor?.commands.clearContent();
      setTitle('');
      setEditingId(null);
      setReloadKey((k) => k + 1);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save note');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="mx-auto max-w-5xl animate-fade-in space-y-6">
      <p className="text-sm text-fg-muted">
        Capture how you think and work. We refine, then extract the method — not the content.
      </p>
      <form className="card space-y-5" onSubmit={submit}>
        <label className="block">
          <span className="field-label">
            Title <span className="font-normal text-fg-subtle">(optional)</span>
          </span>
          <input
            className="input"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            aria-label="Note title"
            placeholder="e.g. How I run design reviews"
          />
        </label>

        <div>
          <span className="field-label">Note</span>
          <div
            className="rounded-xl border border-line/15 bg-surface p-3.5 shadow-xs transition focus-within:border-brand/60 focus-within:ring-4 focus-within:ring-brand/15 [&_.ProseMirror]:min-h-44 [&_.ProseMirror]:text-sm [&_.ProseMirror]:leading-relaxed [&_.ProseMirror]:outline-none"
            data-testid="notes-editor"
          >
            <EditorContent editor={editor} />
          </div>
        </div>

        {message ? (
          <p
            className="rounded-xl border border-success/20 bg-success/10 px-3.5 py-2.5 text-sm text-success"
            role="status"
          >
            {message}
          </p>
        ) : null}
        {error ? (
          <p
            className="rounded-xl border border-danger/20 bg-danger/10 px-3.5 py-2.5 text-sm text-danger"
            role="alert"
          >
            {error}
          </p>
        ) : null}

        <div className="flex items-center gap-2">
          <button type="submit" className="btn" disabled={busy}>
            {busy ? 'Saving…' : editingId ? 'Update note' : 'Save note'}
          </button>
          {editingId ? (
            <button type="button" className="btn-ghost" onClick={cancelEdit} disabled={busy}>
              Cancel edit
            </button>
          ) : null}
        </div>
      </form>

      <SourceList
        kind="notes"
        title="Your notes"
        emptyText="No notes yet — write one above."
        reloadKey={reloadKey}
        onEdit={startEdit}
        confirmMessage={(s) =>
          `Delete “${s.label ?? 'this note'}”? Everything distilled from it will be removed.`
        }
      />
    </div>
  );
}
